import type { RealtimeEvent } from '@mfos/shared';
import type { QueryKey } from '@tanstack/react-query';
import { cabinetKey } from '../cabinet/cabinet.api';
import { decisionKey, decisionsKey } from '../decisions/decisions.api';
import { goalsKey } from '../goals/goals.api';
import { meetingsKey } from '../meetings/meetings.api';
import { notificationsKey } from '../notifications/notifications.api';
import { reportsKey } from '../reports/reports.api';
import { blockersKey, taskKey, tasksKey } from '../tasks/tasks.api';
import { updatesKey } from '../updates/updates.api';
import { workspaceKey } from '../workspace/workspace.api';
import { topicCommentsKey, topicDetailKey, topicKey, topicsKey } from '../topics/topics.api';
import { roundsKey } from '../voting/voting.api';

/** Query keys to refetch for one hint. Hints carry ids only; data always comes from a refetch. */
export function keysForEvent(event: RealtimeEvent): QueryKey[] {
  const ws = event.workspaceId;
  const topic = event.topicId;
  switch (event.type) {
    case 'topic.changed':
      return [
        topicsKey(ws),
        ...(topic !== null ? [topicDetailKey(ws, topic)] : []),
        cabinetKey(ws),
        // "Meeting required" topics feed the agenda queue.
        meetingsKey(ws),
      ];
    case 'discussion.changed':
      return topic !== null
        ? [topicCommentsKey(ws, topic), topicDetailKey(ws, topic), topicsKey(ws)]
        : [topicsKey(ws)];
    case 'round.changed':
      return [
        ...(topic !== null ? [roundsKey(ws, topic), topicDetailKey(ws, topic)] : []),
        topicsKey(ws),
        cabinetKey(ws),
        decisionsKey(ws),
      ];
    case 'decision.changed':
      return [
        decisionsKey(ws),
        ...(event.entityId !== null ? [decisionKey(ws, event.entityId)] : []),
        ...(topic !== null ? [topicKey(ws, topic)] : []),
        cabinetKey(ws),
        goalsKey(ws),
      ];
    case 'notification.created':
      return [notificationsKey(ws), cabinetKey(ws)];
    case 'task.changed':
      return [
        tasksKey(ws),
        ...(event.entityId !== null ? [taskKey(ws, event.entityId)] : []),
        ...(topic !== null ? [topicDetailKey(ws, topic)] : []),
        // Decision pages embed their follow-up tasks.
        [...workspaceKey(ws), 'decision'],
        blockersKey(ws),
        updatesKey(ws),
        reportsKey(ws),
        cabinetKey(ws),
        // Meeting agendas list their action items; goal timelines list completed tasks.
        meetingsKey(ws),
        goalsKey(ws),
      ];
    case 'update.changed':
      return [updatesKey(ws), reportsKey(ws), cabinetKey(ws), goalsKey(ws)];
    case 'meeting.changed':
      return [
        meetingsKey(ws),
        ...(topic !== null ? [topicDetailKey(ws, topic)] : []),
        // Meeting action items are tasks.
        tasksKey(ws),
        cabinetKey(ws),
      ];
    case 'goal.changed':
      return [goalsKey(ws)];
  }
}

/** Everything a missed hint could have touched, refetched after the stream reconnects. */
export function resyncKeys(workspaceId: string): QueryKey[] {
  return [
    topicsKey(workspaceId),
    decisionsKey(workspaceId),
    notificationsKey(workspaceId),
    cabinetKey(workspaceId),
    tasksKey(workspaceId),
    blockersKey(workspaceId),
    updatesKey(workspaceId),
    reportsKey(workspaceId),
    meetingsKey(workspaceId),
    goalsKey(workspaceId),
  ];
}
